export const colors = {
  primary: {
    50: '#f0fdfa',
    100: '#ccfbf1',
    200: '#99f6e4',
    300: '#5eead4',
    400: '#2dd4bf',
    500: '#45D0BD',
    600: '#0d9488',
    700: '#0f766e',
    800: '#115e59',
    900: '#134e4a',
  },

  secondary: {
    50: '#faf5ff',
    100: '#f3e8ff',
    200: '#e9d5ff',
    300: '#d8b4fe',
    400: '#c084fc',
    500: '#8258C8',
    600: '#9333ea',
    700: '#7c3aed',
    800: '#6b21a8',
    900: '#581c87',
  },

  success: {
    50: '#f0fdf4',
    100: '#dcfce7',
    200: '#bbf7d0',
    300: '#86efac',
    400: '#4ade80',
    500: '#059669',
    600: '#047857',
    700: '#065f46',
    800: '#064e3b',
    900: '#052e16',
  },

  warning: {
    50: '#fffbeb',
    100: '#fef3c7',
    200: '#fde68a',
    300: '#fcd34d',
    400: '#fbbf24',
    500: '#f59e0b', // Professional amber
    600: '#d97706',
    700: '#b45309',
    800: '#92400e',
    900: '#78350f',
  },

  error: {
    50: '#fef2f2',
    100: '#fee2e2',
    200: '#fecaca',
    300: '#fca5a5',
    400: '#f87171',
    500: '#ef4444',
    600: '#dc2626',
    700: '#b91c1c',
    800: '#991b1b',
    900: '#7f1d1d',
  },

  info: {
    50: '#f0f9ff',
    100: '#e0f2fe',
    200: '#bae6fd',
    300: '#7dd3fc',
    400: '#38bdf8',
    500: '#0ea5e9',
    600: '#0284c7',
    700: '#0369a1',
    800: '#075985',
    900: '#0c4a6e',
  },

  gray: {
    50: '#f9fafb',
    100: '#f3f4f6',
    200: '#e5e7eb',
    300: '#d1d5db',
    400: '#9ca3af',
    500: '#6b7280',
    600: '#4b5563',
    700: '#374151',
    800: '#1F1F1F',
    900: '#0F1010',
  },

  accent: {
    cyan: '#A2F5FF',
    pink: '#E987C4',
    blue: '#44B6E9',
    purple: '#6766C8',
    darkBlue: '#2C84C8',
    neural: '#5F6BC9',
  },

  modern: {
    teal: '#45d0bd',
    blue: '#44b6e9',
    purple: '#8258c8',
    darkBlue: '#2c84c8',
    gray: '#898989',
    lightGray: '#bebebe',
    darkGray: '#717171',
    borderGray: '#adadad',
    textGray: '#9b9b9b',
  },

  // Surface Colors
  surface: {
    light: '#ffffff',
    dark: '#121212',
    paper: '#ffffff',
    paperDark: '#1e1e1e',
  },

  // Text Colors - Exact from HTML designs
  text: {
    primary: '#333333', // Exact from HTML: #333333
    secondary: '#898989', // Exact from HTML: #898989
    disabled: '#9ca3af',
    inverse: '#ffffff',
    inverseSecondary: '#d1d5db',
    light: '#F5F5F5', // Exact from HTML: #F5F5F5
    muted: '#BEBEBE', // Exact from HTML: #BEBEBE
  },

  // Background Colors
  background: {
    default: '#ffffff',
    paper: '#ffffff',
    dark: '#0F1010',
    darkPaper: '#1F1F1F',
  },

  // Border Colors
  border: {
    light: '#e5e7eb',
    medium: '#d1d5db',
    dark: '#374151',
    focus: '#45D0BD',
  },
} as const;

// Color utility functions
export const getColor = (colorPath: string): string => {
  const keys = colorPath.split('.');
  let value: Record<string, unknown> = colors;

  for (const key of keys) {
    value = value?.[key] as Record<string, unknown>;
    if (value === undefined) {
      return '#000000';
    }
  }

  return value as unknown as string;
};

// Semantic color mappings
export const semanticColors = {
  brand: colors.primary[500],
  accent: colors.secondary[500],
  success: colors.success[500],
  warning: colors.warning[500],
  error: colors.error[500],
  info: colors.info[500],
} as const;

// Dark mode color overrides
export const darkModeColors = {
  surface: {
    light: colors.gray[800],
    dark: colors.gray[900],
    paper: colors.gray[800],
    paperDark: colors.gray[900],
  },
  text: {
    primary: colors.gray[100],
    secondary: colors.gray[300],
    disabled: colors.gray[500],
    inverse: colors.gray[900],
    inverseSecondary: colors.gray[600],
  },
  background: {
    default: colors.gray[900],
    paper: colors.gray[800],
    dark: colors.gray[900],
    darkPaper: colors.gray[800],
  },
} as const;
